import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import SelectableCard from "./SelectableCard";
import AddTreatmentModal from "./AddTreatmentModal";
import ClinicalReferenceSources from "./ClinicalReferenceSources";
import Plan2CategoryTable, { Plan2ModeToggle } from "./Plan2CategoryTable";
import Plan2HintsPanel from "./Plan2HintsPanel";
import {
  TREATMENT2_BUNDLE_CATEGORIES,
  TREATMENT2_TOGGLE_LABELS,
  buildPlan2Hints,
  createEmptyTreatment2ByCategory,
  ensurePlan2Defaults,
  fillAllPlan2Ordered,
  getTreatment2BulkActionLabel,
  normalizePlan2Values,
  treatment2GroupKey,
} from "./plan2Catalog";

// ============================================================================
// Types
// ============================================================================

export interface Treatment {
  id: string;
  name: string;
  category:
    | "medication"
    | "procedure"
    | "therapy"
    | "lifestyle"
    | "referral"
    | "other";
  priority: "first-line" | "alternative" | "adjunct";
  urgency?: "stat" | "urgent" | "routine";
  rationale: string;
  diagnosis?: string | string[];
  route?: string; // For medications
  contraindications?: string[];
  mutuallyRedundantGroup?: string; // Group ID for "Pick N" groups
  pickCount?: number; // How many to pick from the group
  linkedDiagnoses: string[];
}

export interface TreatmentPlanData {
  treatments: Treatment[];
  treatmentStrategy?: string;
  considerations?: string[];
}

interface TreatmentPlanStepProps {
  selectedDiagnoses: any[];
  planData: TreatmentPlanData | null;
  loading: boolean;
  error: string | null;
  hasTreatments: boolean;
  retry: () => void;
  disabledTreatmentIds: Set<string>;
  onToggleTreatmentDisabled: (id: string) => void;
  selectedTreatments: Set<string>;
  onToggleTreatmentSelected: (id: string) => void;
  customTreatments?: Treatment[];
  onAddCustomTreatment?: (treatment: Treatment) => void;
  clinicalReferences?: any[];
  embeddedDocuments?: any[];
  /** Heading override, e.g. for MorningReport's teaching cases. */
  heading?: { title: string; subtitle: string };
  /**
   * "page" = standalone h4 (default). "section" = subtitle under a parent page title
   * (Events note type Plan: Treatments under Event Management Approach).
   */
  headingLevel?: "page" | "section";
  /**
   * Events keep/hide: no checkbox select — eye exclude/restore is the only filter; session
   * syncs selectedTreatments from non-excluded items.
   */
  filterMode?: boolean;
  /** Puts a pencil on each card, as the Tests step does for results (inpatient Daily Plan notes). */
  onEditTreatment?: (treatment: Treatment) => void;
  editTooltip?: string;
  /** Extra content inside each card, e.g. the Daily Plan note once one is entered. */
  renderTreatmentNote?: (treatment: Treatment) => React.ReactNode;
  /** Treatment2 table mode by group (medication / therapy / treatmentsBundle). */
  treatment2ByCategory?: Record<
    string,
    { mode?: "list" | "table"; values?: Record<string, string[]>; pendingFlip?: Record<string, boolean> }
  >;
  onTreatment2ModeChange?: (groupKey: string, mode: "list" | "table", itemIds?: string[]) => void;
  onTreatment2ItemValueChange?: (groupKey: string, itemId: string, value: string[]) => void;
  onTreatment2ItemFocus?: (groupKey: string, itemId: string) => void;
  onTreatment2ItemBlur?: (groupKey: string, itemId: string) => void;
  onTreatment2DocumentNormal?: (groupKey: string, itemIds?: string[]) => void;
  /**
   * Page-level Clinical Hints (Management). When defined, hides per-section hint cards and
   * follows this open state under *2 rows.
   */
  clinicalHintsOpen?: boolean;
  /**
   * Admission/Progress: lock groups to *2 tables, hide list|*2 toggles, label without " 2".
   */
  plan2Only?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_LABELS: Record<string, string> = {
  other: "Diagnostics",
  medication: "Medications",
  procedure: "Procedures",
  therapy: "Therapies",
  lifestyle: "Lifestyle & Supportive Care",
  referral: "Referrals & Consultations",
};

// ============================================================================
// Treatment Card Component
// ============================================================================

interface TreatmentCardProps {
  treatment: Treatment;
  disabled: boolean;
  selected: boolean;
  filterMode?: boolean;
  onToggleDisabled: () => void;
  onToggleSelected: () => void;
  onEdit?: () => void;
  editTooltip?: string;
  note?: React.ReactNode;
}

const TreatmentCard: React.FC<TreatmentCardProps> = ({
  treatment,
  disabled,
  selected,
  filterMode = false,
  onToggleDisabled,
  onToggleSelected,
  onEdit,
  editTooltip,
  note,
}) => {
  const chips: Array<{
    label: string;
    color?:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
    variant?: "filled" | "outlined";
  }> = [];

  if (treatment.category === "medication" && treatment.route) {
    chips.push({
      label: treatment.route,
      color: "default",
      variant: "outlined",
    });
  }
  if (treatment.urgency && treatment.urgency !== "routine") {
    chips.push({
      label: treatment.urgency === "stat" ? "STAT" : "Urgent",
      color: treatment.urgency === "stat" ? "error" : "warning",
      variant: "filled",
    });
  }

  const contraindications =
    treatment.contraindications && treatment.contraindications.length > 0 ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
        <WarningAmberRounded sx={{ fontSize: 16, color: "warning.main" }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          Contraindications: {treatment.contraindications.join(", ")}
        </Typography>
      </Box>
    ) : null;

  return (
    <SelectableCard
      title={treatment.name}
      rationale={treatment.rationale}
      selectionVariant={filterMode ? "none" : "checkbox"}
      selected={filterMode ? false : selected}
      disabled={disabled}
      onClick={filterMode ? undefined : onToggleSelected}
      onToggleDisabled={onToggleDisabled}
      hideTooltip="Exclude"
      restoreTooltip="Restore"
      chips={chips}
      onEdit={onEdit}
      editTooltip={editTooltip}
      additionalContent={
        contraindications || note ? (
          <>
            {contraindications}
            {note}
          </>
        ) : null
      }
    />
  );
};

// ============================================================================
// Main Component
// ============================================================================

const TreatmentPlanStep: React.FC<TreatmentPlanStepProps> = ({
  selectedDiagnoses,
  planData,
  loading,
  error,
  hasTreatments,
  retry,
  disabledTreatmentIds,
  onToggleTreatmentDisabled,
  selectedTreatments,
  onToggleTreatmentSelected,
  customTreatments = [],
  onAddCustomTreatment,
  clinicalReferences = [],
  embeddedDocuments = [],
  heading,
  headingLevel = "page",
  filterMode = false,
  onEditTreatment,
  editTooltip,
  renderTreatmentNote,
  treatment2ByCategory,
  onTreatment2ModeChange,
  onTreatment2ItemValueChange,
  onTreatment2ItemFocus,
  onTreatment2ItemBlur,
  onTreatment2DocumentNormal,
  clinicalHintsOpen,
  plan2Only = false,
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const titleVariant = headingLevel === "section" ? "h5" : "h4";
  const [localTreatment2ByCategory, setLocalTreatment2ByCategory] = useState(() =>
    createEmptyTreatment2ByCategory(),
  );
  const [localHintsOpen, setLocalHintsOpen] = useState(false);
  const hintsControlled = clinicalHintsOpen !== undefined;
  const tipsOpen = hintsControlled ? Boolean(clinicalHintsOpen) : localHintsOpen;
  const treatment2Controlled = typeof onTreatment2ModeChange === "function";
  const effectiveTreatment2 = treatment2Controlled
    ? treatment2ByCategory || createEmptyTreatment2ByCategory()
    : localTreatment2ByCategory;

  const updateLocalTreatment2 = (groupKey: string, updater: (current: any) => any) => {
    setLocalTreatment2ByCategory((prev) => {
      const current = prev[groupKey] || createEmptyTreatment2ByCategory()[groupKey];
      return { ...prev, [groupKey]: updater(current) };
    });
  };

  const changeTreatment2Mode = (
    groupKey: string,
    mode: "list" | "table",
    itemIds: string[],
  ) => {
    if (treatment2Controlled) {
      onTreatment2ModeChange?.(groupKey, mode, itemIds);
      return;
    }
    updateLocalTreatment2(groupKey, (current) => {
      let next = { ...current, mode };
      if (mode === "table") {
        next = ensurePlan2Defaults(next, itemIds, { variant: "treatment" });
      }
      return next;
    });
  };

  const changeTreatment2Value = (groupKey: string, itemId: string, value: string[]) => {
    if (treatment2Controlled) {
      onTreatment2ItemValueChange?.(groupKey, itemId, value);
      return;
    }
    updateLocalTreatment2(groupKey, (current) => ({
      ...current,
      values: { ...(current.values || {}), [itemId]: normalizePlan2Values(value) },
    }));
  };

  const documentTreatment2Normal = (groupKey: string, itemIds: string[]) => {
    if (treatment2Controlled) {
      onTreatment2DocumentNormal?.(groupKey, itemIds);
      return;
    }
    updateLocalTreatment2(groupKey, (current) => fillAllPlan2Ordered(current, itemIds));
  };

  const renderCard = (treatment: Treatment) => (
    <TreatmentCard
      key={treatment.id}
      treatment={treatment}
      disabled={disabledTreatmentIds.has(treatment.id)}
      selected={selectedTreatments.has(treatment.id)}
      filterMode={filterMode}
      onToggleDisabled={() => onToggleTreatmentDisabled(treatment.id)}
      onToggleSelected={() => onToggleTreatmentSelected(treatment.id)}
      // Only a checked item can carry a note: the note request lists checked items only, so a
      // note on an unchecked one would show on the card and never reach the chart.
      // Filter mode: kept = not excluded (session sync owns selectedTreatments).
      onEdit={
        onEditTreatment &&
        (filterMode
          ? !disabledTreatmentIds.has(treatment.id)
          : selectedTreatments.has(treatment.id) && !disabledTreatmentIds.has(treatment.id))
          ? () => onEditTreatment(treatment)
          : undefined
      }
      editTooltip={editTooltip}
      note={
        (filterMode
          ? !disabledTreatmentIds.has(treatment.id)
          : selectedTreatments.has(treatment.id) && !disabledTreatmentIds.has(treatment.id))
          ? renderTreatmentNote?.(treatment)
          : null
      }
    />
  );
  // Get mutually redundant group info
  const mutuallyRedundantGroups = useMemo(() => {
    const groups: Record<string, { treatments: Treatment[]; pickCount: number }> = {};

    planData?.treatments.forEach((treatment) => {
      if (treatment.mutuallyRedundantGroup) {
        const groupId = treatment.mutuallyRedundantGroup;
        if (!groups[groupId]) {
          groups[groupId] = {
            treatments: [],
            pickCount: treatment.pickCount || 1,
          };
        }
        groups[groupId].treatments.push(treatment);
      }
    });
    return groups;
  }, [planData?.treatments]);

  const existingTreatmentNames = useMemo(() => [
    ...(planData?.treatments || []),
    ...customTreatments,
  ].map((t) => t.name), [planData?.treatments, customTreatments]);

  const treatmentsByGroup = useMemo(() => {
    const groups: Record<string, Treatment[]> = {
      medication: [],
      therapy: [],
      treatmentsBundle: [],
    };
    (planData?.treatments || []).forEach((treatment) => {
      groups[treatment2GroupKey(treatment.category)].push(treatment);
    });
    return groups;
  }, [planData?.treatments]);

  // Seed Ordered defaults when *2 table mode is already active (default) and items appear.
  useEffect(() => {
    (["medication", "therapy", "treatmentsBundle"] as const).forEach((groupKey) => {
      const items = treatmentsByGroup[groupKey] || [];
      if (!items.length) return;
      const itemIds = items.map((item) => item.id);
      const state = effectiveTreatment2?.[groupKey];
      if (state?.mode !== "table") return;
      const missing = itemIds.some(
        (id) => !Object.prototype.hasOwnProperty.call(state.values || {}, id),
      );
      if (!missing) return;
      changeTreatment2Mode(groupKey, "table", itemIds);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentsByGroup, effectiveTreatment2]);

  const renderTreatmentSegments = (treatments: Treatment[]) => {
    const segments: React.ReactNode[] = [];
    const renderedGroups = new Set<string>();

    treatments.forEach((treatment) => {
      const groupId = treatment.mutuallyRedundantGroup;

      if (groupId) {
        if (renderedGroups.has(groupId)) return;
        renderedGroups.add(groupId);

        const groupInfo = mutuallyRedundantGroups[groupId];
        const groupTreatments = treatments.filter(
          (t) => t.mutuallyRedundantGroup === groupId,
        );

        segments.push(
          <Box
            key={`group-${groupId}`}
            sx={{
              mb: 2,
              pl: 1.5,
              borderLeft: "3px solid",
              borderColor: "warning.main",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "warning.dark",
                display: "block",
                mb: 1,
              }}
            >
              Choose {groupInfo.pickCount}
            </Typography>
            {groupTreatments.map(renderCard)}
          </Box>,
        );
      } else {
        segments.push(renderCard(treatment));
      }
    });

    return segments;
  };

  const renderPlan2Group = (
    groupKey: "medication" | "therapy" | "treatmentsBundle",
    items: Treatment[],
  ) => {
    if (!items.length) return null;
    const labels = TREATMENT2_TOGGLE_LABELS[groupKey];
    const state = effectiveTreatment2?.[groupKey];
    const mode = plan2Only || state?.mode === "table" ? "table" : "list";
    const itemIds = items.map((item) => item.id);

    return (
      <Box key={groupKey} sx={{ mb: 3 }}>
        {plan2Only ? (
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            {labels.list}
          </Typography>
        ) : (
          <Plan2ModeToggle
            listLabel={labels.list}
            tableLabel={labels.table}
            mode={mode}
            onModeChange={(nextMode) =>
              changeTreatment2Mode(groupKey, nextMode as "list" | "table", itemIds)
            }
          />
        )}
        {mode === "table" ? (
          <Plan2CategoryTable
            items={items.map((item) => ({ id: item.id, name: item.name }))}
            values={state?.values || {}}
            pendingFlip={{}}
            variant="treatment"
            tabFillsPreferred={false}
            tipsOpen={tipsOpen}
            tipsByItemId={Object.fromEntries(
              buildPlan2Hints(items).map((hint) => [hint.itemId, [hint.line]]),
            )}
            onChangeItemValue={(itemId, value) =>
              changeTreatment2Value(groupKey, itemId, value)
            }
            onDocumentNormal={() => documentTreatment2Normal(groupKey, itemIds)}
            documentNormalLabel={getTreatment2BulkActionLabel(groupKey)}
          />
        ) : groupKey === "treatmentsBundle" ? (
          TREATMENT2_BUNDLE_CATEGORIES.map((category) => {
            const categoryItems = items.filter(
              (item) => (item.category || "other") === category,
            );
            if (!categoryItems.length) return null;
            return (
              <Box key={category} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  {CATEGORY_LABELS[category] || "Other"}
                </Typography>
                {renderTreatmentSegments(categoryItems)}
              </Box>
            );
          })
        ) : (
          renderTreatmentSegments(items)
        )}
      </Box>
    );
  };

  // No diagnoses selected
  if (selectedDiagnoses.length === 0) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography variant={titleVariant} sx={{ mb: 2 }}>
          {heading?.title ?? "Treatment Plan"}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Select a diagnosis first to generate a treatment plan.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box sx={{ textAlign: headingLevel === "section" ? "left" : "center", mb: 4 }}>
        <Typography variant={titleVariant} sx={{ mb: 1.5 }}>
          {heading?.title ?? "Treatment Plan"}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {heading?.subtitle ??
            (filterMode
              ? "Hide treatments that don't apply. What remains goes into the note."
              : "Click treatments to select them for your plan. Use the hide button to exclude treatments from documentation.")}
        </Typography>
      </Box>

      {/* Add Treatment button — only after loading completes */}
      {!loading && (
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddRounded />}
            onClick={() => setAddModalOpen(true)}
          >
            Add Treatment
          </Button>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ width: "100%", mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={retry}
              startIcon={<RefreshRounded />}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Generating treatment plan...
          </Typography>
        </Box>
      )}

      {/* Custom Treatments — shown above AI-generated sections */}
      {!loading && customTreatments.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Your Treatments
          </Typography>
          {customTreatments.map(renderCard)}
        </Box>
      )}

      {/* Treatments */}
      {!loading && hasTreatments && planData && (
        <Box sx={{ width: "100%" }}>
          {/* Treatment strategy */}
          {planData.treatmentStrategy && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: (theme: any) =>
                  theme.palette.primary.main + "08",
                borderColor: "primary.light",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Treatment Strategy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {planData.treatmentStrategy}
              </Typography>
            </Paper>
          )}

          {!hintsControlled ? (
            <Plan2HintsPanel
              open={tipsOpen}
              onToggle={() => setLocalHintsOpen((prev) => !prev)}
              hasHints={buildPlan2Hints(planData.treatments || []).length > 0}
            />
          ) : null}

          {/* Treatments by Plan2 group — always show Medications|2, Therapies|2, Treatments|2 */}
          {renderPlan2Group("medication", treatmentsByGroup.medication)}
          {renderPlan2Group("therapy", treatmentsByGroup.therapy)}
          {renderPlan2Group("treatmentsBundle", treatmentsByGroup.treatmentsBundle)}

          {/* Considerations */}
          {planData.considerations && planData.considerations.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Clinical Considerations
              </Typography>
              <Box
                component="ul"
                sx={{ m: 0, pl: 2.5, color: "text.secondary" }}
              >
                {planData.considerations.map((item, idx) => (
                  <li key={idx}>
                    <Typography variant="body2">{item}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Source documents that backed the treatment plan */}
      {!loading && (
        <ClinicalReferenceSources
          clinicalReferences={clinicalReferences}
          embeddedDocuments={embeddedDocuments}
        />
      )}

      {/* Empty state */}
      {!loading && !hasTreatments && !error && customTreatments.length === 0 && (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", textAlign: "center", py: 2 }}
        >
          No treatments generated.
        </Typography>
      )}

      <AddTreatmentModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={(treatment) => {
          onAddCustomTreatment?.(treatment);
        }}
        existingNames={existingTreatmentNames}
      />
    </Box>
  );
};

export default TreatmentPlanStep;
